import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { PhoneOutgoing, ThumbsUp, Clock, ThumbsDown, Loader2, ArrowLeft } from 'lucide-react';

const OUTCOMES = [
  { value: 'interested', label: 'Interested', desc: 'Positive call — the letter is marked as Responded and counts as a phone response in the A/B stats.', seqDesc: 'Positive call — counts as a phone response and the sequence stops automatically.', icon: ThumbsUp, cls: 'border-emerald-500/40 hover:border-emerald-500 hover:bg-emerald-500/10 text-emerald-400' },
  { value: 'later', label: 'Try again later', desc: 'Not now — a new follow-up task is created automatically after another 21 days.', seqDesc: 'Not now — the sequence simply continues with the next planned step.', icon: Clock, cls: 'border-amber-500/40 hover:border-amber-500 hover:bg-amber-500/10 text-amber-400' },
  { value: 'no_interest', label: 'No interest', desc: 'Definitive no — the reason is recorded and a pass is proposed on the deal automatically (one confirmation by the other approver).', seqDesc: 'Definitive no — the reason is recorded, the sequence pauses and a pass is proposed on the deal automatically (one confirmation by the other approver).', icon: ThumbsDown, cls: 'border-red-500/40 hover:border-red-500 hover:bg-red-500/10 text-red-400' },
];

export const NO_INTEREST_CATEGORIES = [
  { value: 'no_sale_interest', label: 'No interest in selling' },
  { value: 'solved', label: 'Succession already solved' },
  { value: 'timing', label: 'Timing' },
  { value: 'in_talks', label: 'Already in talks' },
  { value: 'other', label: 'Other' },
];

export function FollowupOutcomeDialog({ task, busy, onSelect, onSkip, onClose }) {
  const [reasonStep, setReasonStep] = useState(false);
  const [category, setCategory] = useState(null);
  const [reasonText, setReasonText] = useState('');
  const seqMode = ((task?.template_id) || '').startsWith('seq:');

  const reset = () => { setReasonStep(false); setCategory(null); setReasonText(''); };
  const handleClose = () => { reset(); onClose(); };

  const pick = (value) => {
    if (value === 'no_interest') { setReasonStep(true); return; }
    onSelect(value);
  };

  const confirmNoInterest = () => {
    onSelect('no_interest', { category, reason: reasonText.trim() });
    reset();
  };

  return (
    <Dialog open={!!task} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="followup-outcome-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
            {reasonStep ? (
              <>
                <button onClick={reset} className="text-muted-foreground hover:text-foreground" data-testid="followup-reason-back">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <ThumbsDown className="w-5 h-5 text-red-400" /> Why no interest?
              </>
            ) : (
              <><PhoneOutgoing className="w-5 h-5 text-orange-400" /> How did the call go?</>
            )}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">{task?.title}</p>

        {reasonStep ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {NO_INTEREST_CATEGORIES.map(c => (
                <button key={c.value} disabled={busy} onClick={() => setCategory(c.value)}
                  data-testid={`no-interest-category-${c.value}`}
                  className={`text-left border rounded-sm px-3 py-2 text-xs transition-colors ${category === c.value
                    ? 'border-red-500 bg-red-500/10 text-red-300 font-medium'
                    : 'border-border bg-secondary/20 text-foreground hover:border-red-500/50'}`}>
                  {c.label}
                </button>
              ))}
            </div>
            <textarea value={reasonText} onChange={e => setReasonText(e.target.value)} rows={2}
              placeholder="Optional details — leave empty to auto-extract the reason from the linked Plaud note (if any)..."
              data-testid="no-interest-reason-input"
              className="w-full bg-secondary/40 border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:border-red-700 resize-none" />
            <Button onClick={confirmNoInterest} disabled={busy || !category} data-testid="no-interest-confirm-btn"
              className="w-full bg-red-700 hover:bg-red-600 text-white rounded-sm text-xs h-8">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save reason & complete task'}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {OUTCOMES.map(o => (
              <button key={o.value} disabled={busy} onClick={() => pick(o.value)}
                data-testid={`followup-outcome-${o.value}`}
                className={`w-full text-left border rounded-sm px-3.5 py-2.5 transition-colors flex items-start gap-3 bg-secondary/20 ${o.cls}`}>
                <o.icon className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  <span className="text-sm font-medium block">{o.label}</span>
                  <span className="text-[11px] text-muted-foreground block mt-0.5">{seqMode ? o.seqDesc : o.desc}</span>
                </span>
              </button>
            ))}
          </div>
        )}

        {!reasonStep && (
          <div className="flex justify-between items-center mt-1">
            <button onClick={onSkip} disabled={busy} data-testid="followup-outcome-skip"
              className="text-[11px] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Complete without result'}
            </button>
            <Button variant="ghost" onClick={handleClose} disabled={busy} className="text-xs h-8 px-3">Cancel</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
